import { ReactNode, useCallback, useEffect } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { useHistoryShortcuts } from "../../builder/useHistoryShortcuts";
import { useGlobalReducer } from "../../../GlobalContext";
import { ActiveDrag } from "./journeyPlanner.types";
import { useJourneyPlanner } from "./JourneyPlannerContext";
import {
  journeyPlannerCollisionDetection,
  loadJourneyPlanner as loadJourneyPlannerData,
  useJourneyPlannerDragEnd,
  useJourneyPlannerNavigation,
  useJourneyPlannerNotification,
  useJourneyPlannerSave,
} from "./journeyPlannerActions";
import { defaultJourneyName } from "./journeyPlannerState";

function JourneyPlanner({ children }: { children: ReactNode }) {
  const globalDispatch = useGlobalReducer();
  const {
    addAutomationToJourney,
    handleDeleteJourney,
    isDirty,
    journeyPendingDelete,
    plannerRef,
    journeyRefs,
    redo,
    planner,
    replaceJourney,
    reorderJourneyItems,
    saving,
    setActiveDrag,
    setIsDirty,
    setJourneyPendingDelete,
    setLoading,
    setPlannerState,
    setPlannerNavigation,
    setSaving,
    undo,
    zoomLevel,
  } = useJourneyPlanner();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );

  const loadPlanner = useCallback(async () => {
    await loadJourneyPlannerData({
      setIsDirty,
      setLoading,
      setPlanner: setPlannerState,
    });
  }, [setIsDirty, setLoading, setPlannerState]);

  useEffect(() => {
    loadPlanner();
  }, [loadPlanner]);

  useHistoryShortcuts(undo, redo);

  const showNotification =
    useJourneyPlannerNotification(globalDispatch);

  const handleSavePlanner = useJourneyPlannerSave({
    globalDispatch,
    plannerRef,
    setIsDirty,
    setPlanner: setPlannerState,
    setSaving,
  });

  useJourneyPlannerNavigation({
    handleSavePlanner,
    isDirty,
    saving,
    setPlannerNavigation,
  });

  const handleDragEnd = useJourneyPlannerDragEnd({
    addAutomationToJourney,
    journeyRefs,
    planner,
    replaceJourney,
    reorderJourneyItems,
    setActiveDrag,
    showNotification,
    zoomLevel,
  });

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={journeyPlannerCollisionDetection}
        onDragStart={(event) =>
          setActiveDrag({
            ...(event.active.data.current as ActiveDrag),
            delta: { x: 0, y: 0 },
          })
        }
        onDragMove={(event) =>
          setActiveDrag((current) =>
            current
              ? {
                  ...current,
                  delta: event.delta,
                }
              : current
          )
        }
        onDragCancel={() => setActiveDrag(null)}
        onDragEnd={handleDragEnd}
      >
        {children}
      </DndContext>

      {journeyPendingDelete && (
        <ConfirmationModal
          confirmText="Delete"
          modalTitle="Delete Journey"
          onClose={() => setJourneyPendingDelete(null)}
          onConfirm={() =>
            handleDeleteJourney(journeyPendingDelete.Id)
          }
        >
          Are you sure you want to delete{" "}
          <strong>
            {journeyPendingDelete.Name || defaultJourneyName}
          </strong>
          ?
        </ConfirmationModal>
      )}
    </>
  );
}

export default JourneyPlanner;
